import { Fragment, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FilePlus, Receipt as ReceiptIcon, PencilSimple, ArrowUUpLeft, HandCoins } from '@phosphor-icons/react';
import AdvanceRefundDialog from '@/components/AdvanceRefundDialog';
import QuickFinanceDialog from '@/components/QuickFinanceDialog';
import ReceiptWithAllocationsDialog from '@/components/ReceiptWithAllocationsDialog';

const fmt = (n) => `\u20B9${(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * CustomerLedgerDialog — single source-of-truth ledger viewer reused by
 * Customers, Create Invoice and Edit Invoice pages so every entry point shows
 * an identical statement.
 *
 * Props:
 *   open                 – dialog visibility
 *   onOpenChange(flag)   – close callback
 *   customer             – { id, name } for the customer being viewed
 *   variant              – 'full' (default, with Quick Actions + edit icons,
 *                           used by /customers) or 'invoice' (read-only,
 *                           used inside the Invoice form to avoid losing the
 *                           in-progress draft when the user navigates away)
 *   onReceivePayment(entry) – called when the user clicks the "Receive ₹X"
 *                              total-due button (Customers page wires this to
 *                              its own Receive Payment dialog). When omitted
 *                              the button is hidden.
 *   onEditInvoice(invoiceId) – called when the user clicks the pencil icon
 *                              on an invoice row. When omitted the icon is
 *                              not rendered.
 *   onEditReceipt(entry)  – called when the user clicks the pencil icon on
 *                            a payment row.
 */
export default function CustomerLedgerDialog({
  open, onOpenChange, customer, variant = 'full',
  onReceivePayment, onEditInvoice, onEditReceipt,
  // When false, the "Collect Due" button is hidden. Used on the Invoice
  // Management page where the ledger is a quick reference and the user
  // is meant to record receipts from the dedicated Receipts page.
  showCollectDue = true,
  // When true, the "Receipt" button opens the FULL Receipt form (with
  // bill-wise adjustment table) instead of the slim QuickFinanceDialog.
  // Used by the Invoice Management page.
  useFullReceipt = false,
  // Fires whenever an action inside the ledger mutates server state
  // (receipt saved, advance refunded, due collected, etc.) so the page
  // hosting the ledger can refetch its own data — e.g. the Invoice
  // Management table reloading payment-status pills on each open invoice.
  onChanged,
}) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  // Collect Due → opens the small Receipt popup pre-filled with the
  // pending due amount. Mirrors the Finance > Receipt page form but in a
  // compact dialog so the user lands right back on the ledger after save.
  const [collectDueOpen, setCollectDueOpen] = useState(false);
  // Plain Receipt button — same compact popup, but with no pre-filled
  // amount. The user keys in whatever they want to park as advance or
  // tag against a specific invoice.
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!open || !customer?.id) return;
    setLoading(true);
    api.get(`/customers/${customer.id}/ledger`)
      .then(r => setEntries(r.data || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [open, customer?.id, reloadKey]);

  const { totalBilled, totalReceived, pendingDue, advanceHeld, closing, totalDebit, totalCredit } = useMemo(() => {
    // Accounting-style aggregation so the header chips match the
    // closing-balance row at the bottom:
    //   • Billed   = sum of invoice debits (work actually billed)
    //   • Received = sum of money in — invoice payments + standalone
    //                advance receipts + any direct credits — minus
    //                advance refunds we paid back out.
    //   • Net      = Received − Billed. Positive = customer is in credit
    //                (we owe / hold an advance), negative = customer owes.
    //   • Due / Advance are derived from Net so the single net-position
    //     chip in the header is always consistent with the closing row.
    let billed = 0, received = 0, debit = 0, credit = 0;
    for (const e of entries) {
      debit += e.debit || 0;
      credit += e.credit || 0;
      if (e.type === 'invoice') {
        billed += e.debit || 0;
      } else if (e.type === 'payment') {
        received += e.credit || 0;
      } else if (e.type === 'advance_received') {
        received += e.credit || 0;
      } else if (e.type === 'advance_refund') {
        // Refunding an advance is money going back out — subtract from received.
        received -= e.debit || 0;
      }
      // advance_used entries are internal reclassifications (advance → bill);
      // they don't move the customer's net position so we ignore them here.
    }
    const last = entries[entries.length - 1];
    const closingVal = last?.balance ?? 0;
    const net = received - billed;
    return {
      totalBilled: billed,
      totalReceived: received,
      pendingDue: Math.max(0, -net),
      advanceHeld: Math.max(0, net),
      closing: closingVal,
      totalDebit: debit,
      totalCredit: credit,
    };
  }, [entries]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl border-[#E4E4E7] !max-w-[98vw] !w-[98vw] !h-[96vh] sm:!max-w-[98vw] sm:!w-[98vw] sm:!h-[96vh] p-5 sm:p-6 flex flex-col gap-3 overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-xl">Customer Ledger &mdash; {customer?.name}</DialogTitle>
          {/* Summary chips + action buttons sit on the SAME row — chips
              on the left, buttons hugging the right. Buttons are only
              rendered in the "full" variant. */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
            <DialogDescription className="text-xs leading-relaxed">
              Billed: <span className="font-mono">{fmt(totalBilled)}</span> &nbsp;|&nbsp;
              Received: <span className="font-mono">{fmt(totalReceived)}</span> &nbsp;|&nbsp;
              {/* Single net-position chip — show receivable when the
                  customer owes us, and switch to "Advance" when they've
                  paid more than billed (we're holding their credit).
                  Replaces the separate "Advance Held" chip. */}
              {pendingDue > 0 ? (
                <>Due: <span className="font-mono text-[#EF4444] font-semibold">{fmt(pendingDue)}</span></>
              ) : advanceHeld > 0 ? (
                <>Advance: <span className="font-mono text-[#3B82F6] font-semibold">{fmt(advanceHeld)}</span></>
              ) : (
                <>Due: <span className="font-mono text-[#10B981]">{fmt(0)}</span></>
              )}
            </DialogDescription>
            {variant === 'full' && customer?.id && (
              <div className="flex items-center gap-2 shrink-0" data-testid="ledger-quick-actions">
                <Button size="sm" variant="outline"
                  className="rounded-lg h-7 px-2.5 text-[11px] border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF]"
                  onClick={() => { onOpenChange?.(false); navigate(`/invoices/create?customer=${encodeURIComponent(customer.id)}&return_to_ledger=1`); }}
                  data-testid="ledger-create-invoice-btn">
                  <FilePlus size={12} className="mr-1" /> Create Invoice
                </Button>
                <Button size="sm" variant="outline"
                  className="rounded-lg h-7 px-2.5 text-[11px] border-[#10B981] text-[#10B981] hover:bg-[#ECFDF5]"
                  onClick={() => setReceiptOpen(true)}
                  data-testid="ledger-receipt-btn">
                  <ReceiptIcon size={12} className="mr-1" /> Receipt
                </Button>
                {pendingDue > 0 && showCollectDue && (
                  <Button size="sm" variant="outline"
                    className="rounded-lg h-7 px-2.5 text-[11px] border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] font-semibold"
                    onClick={() => setCollectDueOpen(true)}
                    data-testid="ledger-collect-due-btn"
                    title={`Collect ${fmt(pendingDue)} pending due`}>
                    <HandCoins size={12} className="mr-1" /> Collect Due
                  </Button>
                )}
                {advanceHeld > 0 && (
                  <Button size="sm" variant="outline"
                    className="rounded-lg h-7 px-2.5 text-[11px] border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2]"
                    onClick={() => setRefundOpen(true)}
                    data-testid="ledger-refund-btn"
                    title={`Refund up to ${fmt(advanceHeld)} held as advance`}>
                    <ArrowUUpLeft size={12} className="mr-1" /> Refund {fmt(advanceHeld)}
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <p className="text-xs text-[#52525B] py-4 text-center">Loading...</p>
        ) : entries.length === 0 ? (
          <p className="text-xs text-[#52525B] py-4 text-center">No transactions</p>
        ) : (
          <div className="flex-1 overflow-auto rounded-lg border border-[#E4E4E7] relative">
            <Table className="text-xs">
              <TableHeader className="sticky top-0 bg-[#0F172A] z-20">
                <TableRow>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-bold text-white py-2">Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-bold text-white py-2">Description</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-bold text-white text-right py-2">Debit</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-bold text-white text-right py-2">Credit</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-bold text-white text-right py-2">Balance</TableHead>
                  {(onEditInvoice || onEditReceipt) && (
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] font-bold text-white text-center w-10 py-2">Edit</TableHead>
                  )}
                </TableRow>
                {/* Opening balance — pinned right under the header so it
                    stays visible while the user scrolls. The Tailwind
                    arbitrary value `top-[33px]` lines it up under the
                    sticky header row. */}
                <TableRow className="bg-gradient-to-r from-[#E0F2FE] to-[#DBEAFE] font-bold border-b-2 border-[#3B82F6]">
                  <TableCell className="text-xs text-[#1E3A8A] py-1.5">{entries[0]?.date ? new Date(entries[0].date).toLocaleDateString('en-IN') : '-'}</TableCell>
                  <TableCell className="text-xs text-[#1E3A8A] uppercase tracking-[0.15em] py-1.5">Opening Balance</TableCell>
                  <TableCell className="text-xs text-right font-mono text-[#1E3A8A] py-1.5">-</TableCell>
                  <TableCell className="text-xs text-right font-mono text-[#1E3A8A] py-1.5">-</TableCell>
                  <TableCell className="text-xs text-right font-mono text-[#1E3A8A] py-1.5" data-testid="ledger-opening-balance">{fmt(0)}</TableCell>
                  {(onEditInvoice || onEditReceipt) && <TableCell className="py-1.5" />}
                </TableRow>
              </TableHeader>
              <TableBody>

                {entries.map((entry, i) => {
                  const isAdvance = entry.type === 'advance_received' || entry.type === 'advance_used' || entry.type === 'advance_refund';
                  const isAdjustment = entry.type === 'adjustment';
                  const isDraft = entry.type === 'draft';
                  const rowClass = entry.type === 'payment'
                    ? 'bg-[#F0FDF4]'
                    : isAdvance
                      ? 'bg-[#EFF6FF]'
                      : isAdjustment
                        ? 'bg-[#F8FAFC] italic'
                        : isDraft
                          ? 'bg-[#FEF3C7]'
                          : '';
                  const adjustments = entry.applied_against || [];
                  const editColCount = (onEditInvoice || onEditReceipt) ? 1 : 0;
                  return (
                    <Fragment key={i}>
                    <TableRow className={rowClass}>
                      <TableCell className="text-xs text-[#52525B] py-1.5">{new Date(entry.date).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell className="text-xs py-1.5">
                        {entry.type === 'invoice' ? (
                          <span><span className="font-mono text-[#3B82F6] font-semibold">{entry.invoice_number}</span> &mdash; {entry.description.split(' - ').slice(1).join(' - ') || 'Invoice'}</span>
                        ) : entry.type === 'payment' ? (
                          <span className="text-[#10B981]">{entry.receipt_number ? <span className="font-mono text-[#059669] font-semibold mr-1">{entry.receipt_number}</span> : null}Payment for {entry.invoice_number}</span>
                        ) : isAdjustment ? (
                          <span className="text-[#64748B]">
                            <span className="px-1.5 py-0.5 rounded bg-[#64748B]/15 text-[10px] font-bold mr-1.5">ADJ</span>
                            {entry.description}
                          </span>
                        ) : isDraft ? (
                          <span className="text-[#92400E]">
                            <span className="px-1.5 py-0.5 rounded bg-[#F59E0B]/30 text-[#92400E] text-[10px] font-bold mr-1.5 uppercase tracking-wide">Draft</span>
                            {entry.description?.replace(/^Draft - /, '') || 'Draft invoice'}
                            {entry.reason && <span className="text-[10px] text-[#A16207] italic ml-2">- {entry.reason}</span>}
                          </span>
                        ) : (
                          <span className="text-[#3B82F6]">
                            {entry.invoice_number
                              ? <span className="font-mono text-[#3B82F6] font-semibold mr-1">{entry.invoice_number}</span>
                              : <span className="px-1.5 py-0.5 rounded bg-[#3B82F6]/15 text-[10px] font-bold mr-1.5">RECEIPT</span>}
                            {entry.description || (entry.type === 'advance_received' ? 'Advance received' : 'Advance applied')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-right font-mono py-1.5">{(entry.debit || 0) > 0 ? fmt(entry.debit) : ''}</TableCell>
                      <TableCell className="text-xs text-right font-mono text-[#10B981] py-1.5">{(entry.credit || 0) > 0 ? fmt(entry.credit) : ''}</TableCell>
                      <TableCell className={`text-xs text-right font-mono font-semibold py-1.5 ${entry.balance > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{fmt(entry.balance || 0)}</TableCell>
                      {(onEditInvoice || onEditReceipt) && (
                        <TableCell className="text-center py-1.5">
                          {onEditInvoice && entry.type === 'invoice' && (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#52525B] hover:text-[#3B82F6]"
                              onClick={() => { onOpenChange?.(false); onEditInvoice(entry.invoice_id); }}
                              title="Edit invoice"
                              data-testid={`edit-inv-${entry.invoice_id}`}>
                              <PencilSimple size={13} />
                            </Button>
                          )}
                          {onEditInvoice && isDraft && entry.draft_id && (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#52525B] hover:text-[#F59E0B]"
                              onClick={() => { onOpenChange?.(false); navigate(`/invoices/create?draft=${entry.draft_id}`); }}
                              title="Open draft"
                              data-testid={`open-draft-${entry.draft_id}`}>
                              <PencilSimple size={13} />
                            </Button>
                          )}
                          {onEditReceipt && entry.type === 'payment' && entry.receipt_number && (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#52525B] hover:text-[#10B981]"
                              onClick={() => onEditReceipt(entry)}
                              title={`Edit ${entry.receipt_number}`}
                              data-testid={`edit-rcpt-${entry.receipt_number}`}>
                              <PencilSimple size={13} />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                    {adjustments.map((adj, k) => (
                      <TableRow key={`${i}-adj-${k}`} className={`${rowClass} border-t-0`} data-testid={`adj-mini-${adj.sub_voucher}`}>
                        <TableCell className="text-[10px] text-[#A1A1AA] pl-6 py-1">
                          {adj.date ? new Date(adj.date).toLocaleDateString('en-IN') : ''}
                        </TableCell>
                        <TableCell className="text-[10px] text-[#52525B] italic pl-6 py-1" colSpan={3 + editColCount}>
                          <span className="text-[#A1A1AA] mr-1">&#8627;</span>
                          <span className="font-mono text-[#64748B] mr-1.5">{adj.sub_voucher}</span>
                          {fmt(adj.amount)} applied to
                          {adj.invoice_number ? <span className="font-mono text-[#3B82F6] ml-1">{adj.invoice_number}</span> : <span className="ml-1">—</span>}
                        </TableCell>
                        <TableCell className="text-[10px] text-[#A1A1AA] text-right font-mono py-1">{/* balance unchanged */}</TableCell>
                      </TableRow>
                    ))}
                    </Fragment>
                  );
                })}
              </TableBody>

              {/* Closing balance — sticky at the bottom of the scroll
                  container, full-bleed coloured bar. Uses a separate
                  <tfoot> so its position is independent of TableBody scroll. */}
              {(() => {
                const last = entries[entries.length - 1];
                const isDue = closing > 0;
                const barClass = isDue
                  ? 'bg-gradient-to-r from-[#FEE2E2] via-[#FCA5A5]/60 to-[#FEE2E2] border-t-4 border-b-4 border-[#EF4444]'
                  : 'bg-gradient-to-r from-[#D1FAE5] via-[#6EE7B7]/60 to-[#D1FAE5] border-t-4 border-b-4 border-[#10B981]';
                const txt = isDue ? 'text-[#7F1D1D]' : 'text-[#064E3B]';
                return (
                  <tfoot className="sticky bottom-0 z-20">
                    <tr className={`${barClass} font-bold`} data-testid="ledger-closing-row">
                      <td className={`text-xs ${txt} py-2 px-4`}>{last?.date ? new Date(last.date).toLocaleDateString('en-IN') : '-'}</td>
                      <td className={`text-xs uppercase tracking-[0.18em] font-extrabold ${txt} py-2 px-4`}>Closing Balance</td>
                      <td className={`text-xs text-right font-mono ${txt} py-2 px-4`}>{fmt(totalDebit)}</td>
                      <td className={`text-xs text-right font-mono ${txt} py-2 px-4`}>{fmt(totalCredit)}</td>
                      <td className={`text-sm text-right font-mono font-extrabold ${txt} py-2 px-4`} data-testid="ledger-closing-balance">{fmt(closing)}</td>
                      {(onEditInvoice || onEditReceipt) && <td className="py-2" />}
                    </tr>
                  </tfoot>
                );
              })()}
            </Table>
          </div>
        )}
      </DialogContent>
      <AdvanceRefundDialog
        key={refundOpen ? `${customer?.id}-open` : 'closed'}
        open={refundOpen}
        onOpenChange={setRefundOpen}
        customer={customer ? { ...customer, advance_balance: advanceHeld } : null}
        onSaved={() => { setReloadKey(k => k + 1); onChanged?.(); }}
      />
      {/* Collect Due — opens the same Receipt form used on the Finance >
          Receipt page (via QuickFinanceDialog) pre-filled with the
          customer + their outstanding due. Closing it bounces back to
          this ledger popup (which stays mounted underneath). */}
      <QuickFinanceDialog
        open={collectDueOpen}
        onOpenChange={setCollectDueOpen}
        mode="receipt"
        defaults={{
          customer_id: customer?.id,
          customer_name: customer?.name,
          amount: pendingDue || 0,
          description: customer ? `Due collected from ${customer.name}` : '',
        }}
        onSaved={() => {
          setCollectDueOpen(false);
          // Bump the reload key so the underlying ledger refetches and
          // the new RCP row + updated due appears immediately.
          setReloadKey(k => k + 1);
          onChanged?.();
        }}
      />
      {/* Plain Receipt button — slim popup, customer pre-filled, amount
          left empty. Rendered only when the caller hasn't opted into the
          full receipt-with-allocations dialog. */}
      {!useFullReceipt && (
        <QuickFinanceDialog
          open={receiptOpen}
          onOpenChange={setReceiptOpen}
          mode="receipt"
          defaults={{ customer_id: customer?.id, customer_name: customer?.name }}
          onSaved={() => { setReceiptOpen(false); setReloadKey(k => k + 1); onChanged?.(); }}
        />
      )}

      {/* Full Receipt with bill-wise allocations — used from Invoice Mgmt
          page so the user can collect + apply against open bills in one
          popup, layered on top of the ledger. Amount field is pre-filled
          with the customer's pending due so the staffer just confirms. */}
      {useFullReceipt && (
        <ReceiptWithAllocationsDialog
          open={receiptOpen}
          onOpenChange={setReceiptOpen}
          customer={customer}
          defaultAmount={pendingDue}
          onSaved={() => { setReceiptOpen(false); setReloadKey(k => k + 1); onChanged?.(); }}
        />
      )}
    </Dialog>
  );
}
